import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

/**
 * Horizontal alignment of the tooltip relative to its control. Use `start` or
 * `end` for controls that sit at the edge of their row, so the bubble stays
 * inside the viewport instead of being clipped.
 */
export type IconButtonTooltipAlign = "center" | "start" | "end";

type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label" | "title">;

export type IconButtonProps = NativeButtonProps & {
  /**
   * Brief description of what the control does. A few words, not a sentence.
   *
   * Supplies both the accessible name and the visible hover text, which is the
   * point: `aria-label` and `title` used to be set independently and drifted
   * apart. Where the accessible name genuinely needs more context, override it
   * with `accessibleName` rather than lengthening the tooltip.
   */
  label: string;
  /**
   * Accessible name, when it needs to be more specific than the tooltip.
   *
   * Tooltips want brevity; accessible names want disambiguation. A grid of
   * media cards should show "Mark as watched" on hover but announce "Mark The
   * Rookie as watched", because a screen reader user tabbing the grid has no
   * other way to tell twenty identical buttons apart. Defaults to `label`.
   */
  accessibleName?: string;
  /** Wrapper class, for layout. Button styling still goes through `className`. */
  wrapperClassName?: string;
  /** Wrapper positioning when the control must sit above another surface. */
  wrapperStyle?: CSSProperties;
  align?: IconButtonTooltipAlign;
  children: ReactNode;
};

/**
 * A button whose visible content is an icon or glyph, with a hover/focus
 * description.
 *
 * The tooltip is a rendered element rather than the native `title` attribute.
 * `title` has a browser-imposed delay of about a second, cannot be styled, does
 * nothing on touch, and — because `aria-label` already supplies the accessible
 * name — gets announced a second time as the description by some screen
 * readers. The bubble here is `aria-hidden`, so assistive tech sees exactly one
 * name and sighted users get an immediate, styled hint.
 *
 * Only for controls with no visible text label. A button that already reads
 * "Save" does not need this. See docs/ui-conventions.md.
 */
export function IconButton({
  label,
  accessibleName,
  wrapperClassName,
  wrapperStyle,
  align = "center",
  children,
  ...buttonProps
}: IconButtonProps) {
  const wrapperClasses = ["icon-button", `icon-button--${align}`, wrapperClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={wrapperClasses} style={wrapperStyle}>
      <button {...buttonProps} aria-label={accessibleName ?? label}>
        {children}
      </button>
      <span className="icon-button__tooltip" aria-hidden="true">
        {label}
      </span>
    </span>
  );
}

export default IconButton;
